import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExtendedMailFolder, MailContext } from './Context';
import { Button, ProgressBar, ProgressSpinner, Tree, TreeNode } from '../../Common/Imports';

export type ExtendedTreeNode = TreeNode & {
    unread: number;
};

function AppMailSidebar() {
    const [items, setItems] = useState<TreeNode[]>([]);
    const [selectedKeys, setSelectedKeys] = useState<string>();
    const [loadingNodes, setLoadingNodes] = useState<{ [key: string]: boolean }>({});
    const navigate = useNavigate();
    const { folders, GetChildrens, folderListIsLoading } = useContext(MailContext);

    const getBadgeValues = useCallback((folders: ExtendedMailFolder[]): TreeNode[] => {
        return folders.map((m): ExtendedTreeNode => ({
            key: m.id || "",
            label: m.displayName ?? "",
            icon: 'pi pi-inbox',
            unread: m.unreadItemCount ?? 0,
            leaf: m.childFolderCount === 0,
            children: m.children ? getBadgeValues(m.children) : []
        }));
    }, []);

    useEffect(() => {
        setItems(getBadgeValues(folders));
    }, [folders, getBadgeValues]);

    const onNodeExpand = async (event: { node: TreeNode }) => {
        const folderId = event.node.key as string;
        setLoadingNodes((prev) => ({ ...prev, [folderId]: true }));

        const expandedFolder = findFolderById(folders, folderId);
        if (expandedFolder && (!expandedFolder.children || expandedFolder.children.length === 0)) {
            await GetChildrens(expandedFolder);
        }

        setLoadingNodes((prev) => ({ ...prev, [folderId]: false }));
    };

    const findFolderById = (folders: ExtendedMailFolder[], id: string | undefined): ExtendedMailFolder | undefined => {
        for (const folder of folders) {
            if (folder.id === id) {
                return folder;
            } else if (folder.children && folder.children.length > 0) {
                const found = findFolderById(folder.children, id);
                if (found) return found;
            }
        }
        return undefined;
    };

    const onNodeSelect = (folderId: string) => {
        console.log(folderId);
        navigate('/mail/' + folderId);
        setSelectedKeys(folderId);
    };

    const nodeTemplate = (data: TreeNode) => {
        const node = data as ExtendedTreeNode;
        const isLoading = node.key ? loadingNodes[node.key] : false;

        return (
            <div className="d-flex align-items-center w-100">
                {node.label}
                {isLoading ? (
                    <div className="ml-auto">
                        <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="4" />
                    </div>
                ) : (
                    node.unread > 0 && (
                        <span className="ml-auto text-sm font-semibold bg-primary-50 text-primary-900 px-2 py-1 hidden md:inline" style={{ borderRadius: '2rem' }}>
                            {node.unread.toString()}
                        </span>
                    )
                )}
            </div>
        );
    };

    return (
        <React.Fragment>
            <div>
                <Button label="Compose New" className="mb-5 w-full" outlined onClick={() => navigate('/mail/new')} />
                <div className="overflow-auto">
                    {
                        folderListIsLoading ?
                            <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
                            :
                            <Tree
                                value={items}
                                className="w-full"
                                selectionMode="single"
                                selectionKeys={selectedKeys}
                                onSelectionChange={(e) => onNodeSelect(e.value as string)}
                                onExpand={onNodeExpand}
                                nodeTemplate={nodeTemplate}
                            />
                    }

                </div>
            </div>
        </React.Fragment>
    );
}

export default AppMailSidebar;
